/**
 * WelcomeFromShare — landing page for QR-code scans from share cards.
 *
 * When a user scans a friend's promotion / streak / PR / sessions share card,
 * the QR code routes them here with a `:context` slug. Each context shows a
 * mirrored brand surface (matches the share card's eyebrow + tone) and a
 * single CTA — claim your spot. Also records the share-source ref to
 * localStorage so we can persist attribution into the user's profile when
 * they register.
 *
 * Anti-shame copy: nothing pressures the visitor. The page is an invitation,
 * not a sales funnel. Single CTA, restrained.
 */

import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Award, Flame, Trophy, Activity } from 'lucide-react';

const CONTEXTS = {
  promotion: {
    Icon: Award,
    eyebrow: 'Someone you know stepped up',
    headline: 'A new rank, tempered.',
    body: 'Promotions are years in the making. Track every step — discipline by discipline — in Enferno.',
    cta: 'Track your own road',
  },
  streak: {
    Icon: Flame,
    eyebrow: 'Someone you know is keeping the fire lit',
    headline: 'Perpetual flame.',
    body: 'Streaks here aren\'t a guilt trip. You define the calendar. You define what counts. Pause when life asks.',
    cta: 'Start your own',
  },
  pr: {
    Icon: Trophy,
    eyebrow: 'Someone you know just held a new line',
    headline: 'Heavier than yesterday.',
    body: 'PR logging that respects the body that made it. See what you can hold — strength, calisthenics, or combatives.',
    cta: 'See what you can hold',
  },
  sessions: {
    Icon: Activity,
    eyebrow: 'Someone you know just put in their reps',
    headline: 'Showing up is the work.',
    body: 'No streaks lost to a missed day. Just the honest record of every session, every art, every season.',
    cta: 'Add yours',
  },
};

const WelcomeFromShare = () => {
  const { context } = useParams();
  const ctx = CONTEXTS[context] || CONTEXTS.promotion;
  const { Icon, eyebrow, headline, body, cta } = ctx;

  // Stash the share-source ref so Register.jsx can lift it into the profile.
  // Per-visitor: last scan wins (a visitor who scans a streak card then a
  // promotion card lands attributed to the promotion).
  useEffect(() => {
    try {
      localStorage.setItem('enferno_share_ref', context || 'promotion');
    } catch { /* ignore — private mode / storage quota */ }
  }, [context]);

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16 flex items-center" data-testid="welcome-from-share">
      <div className="max-w-md mx-auto w-full px-6">
        <div className="mb-8">
          <span className="enf-brand-pill inline-flex items-center">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-12 w-auto"
              loading="eager"
            />
          </span>
        </div>

        <div className="text-enf-accent-fg text-xs uppercase tracking-[0.3em] font-semibold mb-3 flex items-center gap-2" data-testid="welcome-eyebrow">
          <Icon className="w-3.5 h-3.5" /> {eyebrow}
        </div>
        <h1 className="font-display text-5xl tracking-wide leading-none mb-4" data-testid="welcome-headline">{headline}</h1>
        <p className="text-enf-text-secondary mb-10 text-base leading-relaxed" data-testid="welcome-body">{body}</p>

        <Link
          to={`/register?ref=share-${context || 'promotion'}`}
          className="inline-flex items-center gap-2 px-6 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors"
          data-testid="welcome-cta"
        >
          {cta} <ArrowRight className="w-4 h-4" />
        </Link>

        <div className="mt-10 pt-6 border-t border-enf-surface-border text-xs text-enf-text-muted leading-relaxed">
          Enferno is in private beta. We're a small team building the adaptive wellness app we couldn't find — multi-discipline, adaptive-friendly, and honest about what it takes.
        </div>
        <div className="mt-3 text-xs text-enf-text-muted">
          Already have an account? <Link to="/login" className="text-enf-role-info hover:text-enf-text-primary font-semibold">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default WelcomeFromShare;
